import { useEffect, useState } from "react";
import { Box, Button, Chip, CircularProgress, TextField, Typography, useTheme } from "@mui/material";
import { AddRounded, EditOutlined, FavoriteBorderRounded, FavoriteRounded } from "@mui/icons-material";

import { HexTile, SideDrawer, StatusBadge } from "../../../../components/ui";
import { handlePromise } from "../../../../utils/handlePromisse";
import { getProductDetail } from "../../../../services/productService";
import { getOralActive, saveClassification } from "../../../../services/oralService";
import { getClassifications } from "../../../../services/userClassificationsService";
import { productModelToDetails } from "../../../../models/Product.model";
import { oralModelToDetails } from "../../../../models/Oral.model";
import { ActiveModel } from "../active-model";
import { isOral, isVehicle } from "../library-filters";
import { getPeriodLabel } from "../../../../utils/period-label";

// ----------------------------------------------------------------------

/** União dos campos de detalhe tópico (minimum/maximum) e oral (min/max) */
type DrawerDetail = {
  id?: number;
  name?: string;
  mechanism?: string;
  description?: string;
  ph?: string;
  incompatibility?: string;
  /** Tópico */
  minimum?: string;
  /** Tópico */
  maximum?: string;
  /** Oral */
  min?: string;
  /** Oral */
  max?: string;
  /** Oral */
  period?: string;
  source?: string;
  daytime?: number | null;
  nighttime?: number | null;
  vegan?: boolean;
  organic?: boolean;
  pregnant?: boolean;
  classes?: string | null;
  favorite?: boolean;
  annotation?: string;
  showConcentration?: boolean;
  unity?: string;
  percentage?: string;
};

interface ActiveDrawerProps {
  item: ActiveModel | null;
  open: boolean;
  onClose: () => void;
  onToggleFavorite: (item: ActiveModel) => void;
  onAnnotate: (item: ActiveModel) => void;
  onUse: () => void;
}

function SectionLabel({ children }: { children: string }) {
  return (
    <Typography sx={{ mb: 1, fontSize: "11px", fontWeight: 600, letterSpacing: "1px", textTransform: "uppercase", color: "primary.main" }}>
      {children}
    </Typography>
  );
}

function SectionText({ children }: { children: string }) {
  return <Typography sx={{ fontSize: "14px", lineHeight: 1.7, color: "secondary.main" }}>{children}</Typography>;
}

export default function ActiveDrawer({ item, open, onClose, onToggleFavorite, onAnnotate, onUse }: ActiveDrawerProps) {
  const theme = useTheme();
  const [detail, setDetail] = useState<DrawerDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [classifications, setClassifications] = useState<string[]>([]);
  const [userClassifications, setUserClassifications] = useState<string[]>([]);
  const [newClassification, setNewClassification] = useState("");

  useEffect(() => {
    if (!item || !open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setDetail(null);
      if (isOral(item)) {
        const [result] = await handlePromise(getOralActive(item.id));
        if (cancelled) return;
        if (result) {
          setDetail(oralModelToDetails(result.data));
        }
        // Espelha oral-detail.tsx: classificações via getClassifications separado
        const [classResult] = await handlePromise(getClassifications(item.id));
        if (cancelled) return;
        if (classResult) {
          setClassifications(classResult.data.classifications ?? []);
          setUserClassifications(classResult.data.userClassifications ?? []);
        }
      } else {
        const [result] = await handlePromise(getProductDetail(item.id));
        if (cancelled) return;
        if (result) setDetail(productModelToDetails(result.data));
        setClassifications([]);
        setUserClassifications([]);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // item.id + item.type são suficientes para detectar mudança de item;
    // usar o objeto inteiro causaria refetch a cada patch de favorito/anotação
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, item?.type, open]);

  const persistUserClassifications = async (next: string[]) => {
    if (!item) return;
    const [result] = await handlePromise(saveClassification(item.id, next));
    if (result) setUserClassifications(next);
  };

  if (!item) return null;
  const favorite = item.favorite;
  const concVisible = isOral(item) || !!detail?.showConcentration;
  const conc =
    !concVisible
      ? null
      : [detail?.minimum ?? detail?.min, detail?.maximum ?? detail?.max]
          .filter((v) => v !== undefined && v !== null && `${v}` !== "")
          .join(" – ");

  const flags: string[] = [];
  if (detail?.daytime) flags.push("Diurno");
  if (detail?.nighttime) flags.push("Noturno");
  if (detail?.vegan) flags.push("Vegano");
  if (detail?.organic) flags.push("Orgânico");
  if (detail?.pregnant) flags.push("Permitido na gestação");

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      header={
        <Box>
          <Box sx={{ display: "flex", gap: 1.75, alignItems: "flex-start" }}>
            <HexTile label={item.name} size={48} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: theme.typography.fontFamilyDisplay, fontSize: { xs: 22, sm: 25 }, lineHeight: 1.15, color: "text.primary", overflowWrap: "anywhere" }}>
                {item.name}
              </Typography>
              <Box sx={{ mt: "6px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
                <StatusBadge status={isVehicle(item) ? "Veículo" : isOral(item) ? "Oral" : "Tópico"} tone="neutral" />
                {detail?.classes && <StatusBadge status={detail.classes} tone="neutral" />}
              </Box>
            </Box>
          </Box>
          <Box sx={{ mt: "18px", display: "flex", gap: 1.25 }}>
            {!isVehicle(item) && (
              <Button
                fullWidth
                onClick={() => onToggleFavorite(item)}
                startIcon={favorite ? <FavoriteRounded sx={{ color: "primary.main" }} /> : <FavoriteBorderRounded />}
                sx={{ height: 42, border: `1.5px solid ${theme.palette.divider}`, color: "secondary.main" }}
              >
                {favorite ? "Favorito" : "Favoritar"}
              </Button>
            )}
            <Button
              fullWidth
              onClick={() => onAnnotate(item)}
              startIcon={<EditOutlined />}
              sx={{ height: 42, fontWeight: 600, border: `1.5px solid ${theme.palette.primary.main}`, color: "primary.main" }}
            >
              Anotação
            </Button>
          </Box>
        </Box>
      }
      footer={
        <Button fullWidth variant="contained" onClick={onUse} startIcon={<AddRounded />} sx={{ height: 48 }}>
          Usar em nova consulta
        </Button>
      }
    >
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && detail && (
        <>
          {detail.mechanism && (
            <Box sx={{ mb: "22px" }}>
              <SectionLabel>Mecanismo de ação</SectionLabel>
              <SectionText>{detail.mechanism}</SectionText>
            </Box>
          )}
          {detail.description && (
            <Box sx={{ mb: "22px" }}>
              <SectionLabel>Indicações</SectionLabel>
              <SectionText>{detail.description}</SectionText>
            </Box>
          )}

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: "22px" }}>
            {conc && (
              <Box sx={{ backgroundColor: theme.palette.brand.surface2, border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", p: "14px 16px" }}>
                <Typography sx={{ mb: "5px", fontSize: "10.5px", fontWeight: 600, letterSpacing: "1px", textTransform: "uppercase", color: "text.secondary" }}>
                  Concentração usual
                </Typography>
                <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "primary.main" }}>
                  {conc}
                  {` ${detail.unity ?? (isOral(item) ? "" : "%")}`}
                </Typography>
              </Box>
            )}
            {detail.ph && (
              <Box sx={{ backgroundColor: theme.palette.brand.surface2, border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", p: "14px 16px" }}>
                <Typography sx={{ mb: "5px", fontSize: "10.5px", fontWeight: 600, letterSpacing: "1px", textTransform: "uppercase", color: "text.secondary" }}>
                  pH de estabilidade
                </Typography>
                <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "text.primary" }}>{detail.ph}</Typography>
              </Box>
            )}
          </Box>

          {detail.incompatibility && (
            <Box sx={{ mb: "22px" }}>
              <SectionLabel>Incompatibilidades</SectionLabel>
              <SectionText>{detail.incompatibility}</SectionText>
            </Box>
          )}

          {flags.length > 0 && (
            <Box sx={{ mb: "22px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
              {flags.map((flag) => (
                <Chip key={flag} label={flag} size="small" sx={{ backgroundColor: theme.palette.brand.cream, color: "primary.dark", fontWeight: 600 }} />
              ))}
            </Box>
          )}

          {isOral(item) && getPeriodLabel(detail.period) && (
            <Box sx={{ mb: "22px" }}>
              <SectionLabel>Período</SectionLabel>
              <SectionText>{getPeriodLabel(detail.period)}</SectionText>
            </Box>
          )}

          {detail.source && (
            <Box sx={{ mb: "22px" }}>
              <SectionLabel>Fonte</SectionLabel>
              <SectionText>{detail.source}</SectionText>
            </Box>
          )}

          {isOral(item) && (
            <Box sx={{ mb: "22px" }}>
              <SectionLabel>Classificações</SectionLabel>
              <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap", mb: 1 }}>
                {classifications.map((c) => (
                  <Chip key={c} label={c} size="small" />
                ))}
                {userClassifications.map((c) => (
                  <Chip key={c} label={c} size="small" onDelete={() => persistUserClassifications(userClassifications.filter((x) => x !== c))} />
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  size="small"
                  value={newClassification}
                  onChange={(e) => setNewClassification(e.target.value)}
                  placeholder="Nova classificação"
                  inputProps={{ "aria-label": "Nova classificação" }}
                />
                <Button
                  onClick={() => {
                    const value = newClassification.trim();
                    if (!value) return;
                    persistUserClassifications([...userClassifications, value]);
                    setNewClassification("");
                  }}
                >
                  Adicionar
                </Button>
              </Box>
            </Box>
          )}

          {item.annotation && (
            <Box sx={{ mb: "22px" }}>
              <SectionLabel>Sua anotação</SectionLabel>
              <SectionText>{item.annotation}</SectionText>
            </Box>
          )}
        </>
      )}
    </SideDrawer>
  );
}
